/* eslint-disable react-hooks/exhaustive-deps */
import { HubConnectionState } from '@microsoft/signalr';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { BulkMarketplacePriceFormInput } from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeMarketplacePriceAction/BulkChangeMarketplacePriceAction.types';
import {
  buildOfficePriceFormDefaults,
  hasOfficePriceValue,
  OfficePriceRow,
} from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeMarketplacePriceAction/officePriceRows.utils';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useBulkEditHubContext } from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { Stack } from 'src/core/ui';
import { useBulkEditSelectionOffices } from 'src/hooks/api/useBulkEditSelectionOffices';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useSearchV2DataRefetch } from 'src/hooks/useSearchV2DataRefetch';
import { EconomicsSection } from 'src/modals/ListingDetails/components';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getListingDetailsUpdateInput,
  isListingDetailsInputChanged,
} from 'src/utils/inventoryUtils';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Event,
  Listing,
  ListingDetails,
  ListingDetailsUpdateInput,
  ListingQuery,
  MarketplaceAccountSetting,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';

export type BulkEditMarketplacePricesDialogProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  listings?: Listing[] | null;
  selectedListingIds?: number[];
  event?: Event;
  filterQuery?: ListingQuery;
  onOkay: (
    listing: BulkMarketplacePriceFormInput | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel: () => void;
};

const getBulkMarketplacePriceDefaultValues = (
  marketplaceSettings: MarketplaceAccountSetting[] | undefined,
  officePriceRows: OfficePriceRow[]
): BulkMarketplacePriceFormInput => ({
  ...getListingDetailsUpdateInput(null, null, marketplaceSettings),
  // Multi-office selection: one (blank) price row per office; null otherwise
  officePriceUpdates: buildOfficePriceFormDefaults(officePriceRows),
});

export const BulkEditMarketplacePricesDialog = ({
  listings,
  ...rest
}: BulkEditMarketplacePricesDialogProps) => {
  const { loginContext } = useAppContext();
  const marketplaceSettings =
    loginContext?.user?.activeAccount?.marketplaceSettings;
  const {
    offices,
    isLoading: isOfficesLoading,
    hasSelectionError,
  } = useBulkEditSelectionOffices(rest.filterQuery);
  const getDefaultValues = useCallback(
    () => getBulkMarketplacePriceDefaultValues(marketplaceSettings, offices),
    [marketplaceSettings, offices]
  );
  const methods = useForm<BulkMarketplacePriceFormInput>({
    defaultValues: getDefaultValues(),
  });

  const { reset } = methods;
  const officeIdsKey = offices
    .map((o) => `${o.officeId}:${o.isDomestic}`)
    .join(',');
  useEffect(() => {
    // Office rows arrive asynchronously - rebuild the form defaults once the
    // selection's fee rows are known (keyed on ids and buckets so name lookups
    // don't wipe input)
    reset(getDefaultValues());
  }, [officeIdsKey]);

  return (
    <FormProvider {...methods}>
      <BulkEditMarketplacePricesDialogContent
        listings={listings}
        {...rest}
        {...methods}
        officePriceRows={offices}
        isOfficesLoading={isOfficesLoading}
        hasSelectionError={hasSelectionError}
        getDefaultValues={getDefaultValues}
      />
    </FormProvider>
  );
};

const BulkEditMarketplacePricesDialogContent = ({
  officePriceRows,
  isOfficesLoading,
  hasSelectionError,
  getDefaultValues,
  updateKey,
  listings,
  selectedListingIds,
  event,
  onOkay,
  onCancel,
  handleSubmit,
  getValues,
  clearErrors,
  setError,
  setValue,
  watch,
  reset,
  formState,
  ...rest
}: BulkEditMarketplacePricesDialogProps & {
  officePriceRows: OfficePriceRow[];
  isOfficesLoading: boolean;
  hasSelectionError: boolean;
  getDefaultValues: () => BulkMarketplacePriceFormInput;
} & Omit<
    ComponentProps<typeof FormProvider<BulkMarketplacePriceFormInput, unknown>>,
    'children'
  >) => {
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const { searchV2DataDelayedRefetch } = useSearchV2DataRefetch();
  const [isLoading, setIsLoading] = useState(false);
  const input = watch();

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await searchV2DataDelayedRefetch();

        await refreshExpandedListItems();
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshExpandedListItems,
      searchV2DataDelayedRefetch,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        onClose(doneProgress, false);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, stage, preview, initJob } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateMarketplacePrices,
    updateKey,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        onOkay(null, false, onPreviewReceived);
      } else {
        handleSubmit((i) =>
          onOkay(i, hasBackgroundBulkEditFeature, undefined, preview)
        )();
      }
      if (!hasBackgroundBulkEditFeature) {
        setIsLoading(false);
      }
    },
    [stage, onOkay, preview, hasBackgroundBulkEditFeature]
  );

  const isInputValid = useMemo(() => {
    // POS-9555: without the selection's fee rows we cannot price, so block submit rather
    // than fall back to the account-level fee (which would mis-price international listings)
    if (isOfficesLoading || hasSelectionError) return false;
    return (
      isListingDetailsInputChanged(
        input,
        formState.defaultValues as ListingDetailsUpdateInput
      ) || hasOfficePriceValue(input.officePriceUpdates)
    );
  }, [input, formState.defaultValues, isOfficesLoading, hasSelectionError]);

  const submittButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <GenericDialog
        {...rest}
        size="xl"
        header={
          <BulkEditHeader
            headerText={<Content id={ContentId.AllMarketplaces} />}
          />
        }
        onOpened={() => {
          reset(getDefaultValues());
          initJob();
        }}
        onKeyUp={(e) => {
          if (submittButtonRef.current && e.key === 'Enter' && isInputValid) {
            submittButtonRef.current.click();
          }
        }}
        onClosed={() => {
          setMainDialogOpened(false);
          if (progress) {
            setSelectionMode(undefined);
          }
          // Call the outside one if there is one
          rest.onClosed?.();
        }}
        footer={
          isRefreshing ? null : (
            <BulkEditFooter
              entityType={ActionOutboxEntityType.Listing}
              isLoading={isLoading}
              hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
              onClose={onClose}
              onSubmit={onSubmitHandler}
              disabled={!isInputValid}
              submittButtonRef={submittButtonRef}
            />
          )
        }
        onCancel={isLoading ? undefined : onCancel}
      >
        <BulkEditStatus
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isRefreshing || isLoading || isOfficesLoading}
          updateKey={updateKey}
        >
          <Stack direction="column" width="full" gap="s">
            <EconomicsSection
              disabled={isLoading}
              activeEvent={event}
              displayMode={'PRICE_ONLY'}
              isBulkEdit
              officePriceRows={officePriceRows}
              hasSelectionError={hasSelectionError}
            />
          </Stack>
        </BulkEditStatus>
      </GenericDialog>
    </>
  );
};
